import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import useExternalPersonnelText from './externalPersonnelText';
import { COLUMN_KEYS, DEFAULT_FILTERS, MIN_CHARS, normalizeTurkishText, statusOf } from './externalPersonnelColumns';

// Eski sayfanın Firestore sayfa önbelleği (artık kullanılmıyor)
const OLD_PAGE_CACHE_KEY = 'externalPersonnelsPageCache';

/**
 * Data of Harici Personel Yönetimi: every ExternalPersonnels record (ordered
 * by name, read once — the old page paged through Firestore 10 at a time
 * but read the whole collection for its counters anyway), the search (name,
 * surname, phone; 3+ characters), the column filters (3+ characters; status
 * opens on "aktif") and the Excel export of the visible columns.
 */
const useExternalPersonnel = () => {
  const { label, fieldLabel } = useExternalPersonnelText();
  const [personnels, setPersonnels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchPersonnels = useCallback(async () => {
    try {
      const querySnapshot = await getDocs(query(collection(db, 'ExternalPersonnels'), orderBy('Adı')));
      setPersonnels(querySnapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() })));
    } catch (error) {
      console.error('Error fetching external personnels:', error);
      toast.error(label('toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [label]);

  useEffect(() => {
    try {
      localStorage.removeItem(OLD_PAGE_CACHE_KEY);
    } catch (e) { /* storage unavailable */ }
    fetchPersonnels();
    // Yalnızca açılışta
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredPersonnels = useMemo(() => {
    let filtered = personnels;

    // Search (minimum 3 characters): name, surname, phone
    if (searchTerm && searchTerm.trim().length >= MIN_CHARS) {
      const lowercasedSearch = normalizeTurkishText(searchTerm.trim());
      filtered = filtered.filter((personnel) =>
        ['Adı', 'Soyadı', 'Telefon'].some((key) => personnel[key] && normalizeTurkishText(personnel[key]).includes(lowercasedSearch)));
    }

    // Column filters
    Object.keys(filters).forEach((key) => {
      if (!filters[key]) return;
      if (key === 'Durum') {
        // Status always applies (no minimum length)
        filtered = filtered.filter((personnel) => statusOf(personnel[key]) === filters[key]);
      } else {
        // Text filters need at least 3 characters
        const filterValue = String(filters[key]);
        if (filterValue.trim().length >= MIN_CHARS) {
          filtered = filtered.filter((personnel) => normalizeTurkishText(String(personnel[key] || '')).includes(normalizeTurkishText(filterValue.trim())));
        }
      }
    });

    return filtered;
  }, [personnels, filters, searchTerm]);

  const stats = useMemo(() => ({
    total: personnels.length,
    active: personnels.filter((p) => statusOf(p['Durum']) === 'aktif').length,
    inactive: personnels.filter((p) => statusOf(p['Durum']) === 'inaktif').length,
  }), [personnels]);

  const selectedPersonnel = useMemo(() => personnels.find((p) => p.id === selectedId) || null, [personnels, selectedId]);

  const handleFilter = useCallback((column, value) => setFilters((current) => ({ ...current, [column]: value })), []);
  const clearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  /** Excel of every record, with the columns shown in the table (as before). */
  const downloadExcel = async (visibleKeys) => {
    try {
      toast.info(label('toast.excelPreparing'));
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Dış Personeller');
      const keys = COLUMN_KEYS.filter((key) => visibleKeys.includes(key));

      worksheet.columns = keys.map((key) => ({ header: key, key: key.replace(/\s+/g, '_').toLowerCase(), width: 15 }));
      personnels.forEach((personnel) => {
        const rowData = {};
        keys.forEach((key) => { rowData[key.replace(/\s+/g, '_').toLowerCase()] = personnel[key] || ''; });
        worksheet.addRow(rowData);
      });

      // Header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 16, name: 'Calibri' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '92D050' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });
      // Data cells
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.font = { size: 13, name: 'Calibri' };
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          });
        }
      });
      worksheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: worksheet.columns.length } };

      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DisPersoneller_${formattedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(label('toast.excelDone'));
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error(label('toast.excelError'));
    }
  };

  return {
    personnels,
    isLoading,
    filteredPersonnels,
    stats,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    selectedPersonnel,
    setSelectedId,
    downloadExcel,
    refresh: fetchPersonnels,
    fieldLabel,
  };
};

export default useExternalPersonnel;
